import type { Transaction, Auth } from 'oscar-typedef'
import { EthereumPrepareData } from '../prepareData/EthereumPrepareData'
import type { EthereumSendExtraData } from '../sendExtraData/EthereumSendExtraData'
import WepinBackendAPI from '@/networks/api/wepin/WepinBackendAPI'
import type { ITransactionExecutor, EncUVD, AccountToken } from '@/types'

export class EthereumTokenTransactionExecutor implements ITransactionExecutor {
  private accountToken: AccountToken

  constructor(params: { accountToken: AccountToken }) {
    this.accountToken = params.accountToken
  }

  private get gasDecimal() {
    return (
      (this.accountToken.account?.coin?.property?.gasDecimal as number) ?? 9
    )
  }

  async getPrepareData(params: {
    to: string
    amount: string
  }): Promise<EthereumPrepareData> {
    const data =
      await WepinBackendAPI.getInstance().transaction.getTransactionPrepareData(
        {
          accountId: this.accountToken.accountId,
          tokenId: this.accountToken.tokenId?.toString(),
          contract: this.accountToken.coin?.contract ?? undefined,
          to: params.to,
          amount: params.amount,
        },
      )
    return new EthereumPrepareData({
      gasPrice: {
        high: data.detail?.gasPrice.high.toString() ?? '0',
        medium: data.detail?.gasPrice.medium.toString() ?? '0',
        low: data.detail?.gasPrice.low.toString() ?? '0',
      },
      gasLimit: data.detail?.gasLimit ?? 0,
      nonce: data.detail?.nonce ?? 0,
      decimal: this.accountToken.coin?.coin?.decimals ?? 0,
    })
  }

  async sign(params: {
    to: string
    amount: string
    encUVD: EncUVD
    extraData: EthereumSendExtraData
    otpCode?: Auth.VerifyCode.Request.Body
  }): Promise<Transaction.Sign.Response.Body> {
    if (!this.accountToken.account) {
      throw new Error('Account is not exist')
    }
    if (!this.accountToken.coin) {
      throw new Error('Coin is not exist')
    }
    const result = await WepinBackendAPI.getInstance().transaction.sign({
      type: 'transaction',
      walletId: this.accountToken.account.walletId,
      accountId: this.accountToken.account.id,
      tokenId: this.accountToken.id,
      pin: params.encUVD,
      contract: this.accountToken.coin.contract,
      txData: {
        gasLimit: params.extraData.gasLimit,
        gasPrice: params.extraData.gasPrice.amount
          .shiftedBy(-this.gasDecimal)
          .toFixed(),
        to: params.to,
        nonce: params.extraData.nonce,
        amount: params.amount,
        data: '0x',
      },
      otpCode: params.otpCode,
    })
    return result
  }

  async broadcast(params: {
    signResponse: Transaction.Sign.Response.Body
  }): Promise<Transaction.BroadcastTransaction.Response.Body> {
    if (!this.accountToken.account) {
      throw new Error('Account is not exist')
    }
    if (!this.accountToken.coin) {
      throw new Error('Coin is not exist')
    }
    return await WepinBackendAPI.getInstance().transaction.broadcastTransaction(
      {
        walletId: this.accountToken.account.walletId,
        accountId: this.accountToken.accountId,
        contract: this.accountToken.coin.contract,
        tokenId: this.accountToken.id,
        transaction: params.signResponse.transaction,
        signatureResult: params.signResponse.signatureResult,
      },
    )
  }
}
